import { useState, useMemo } from "react"
import {
  Home,
  FileText,
  MapPin,
  Sparkles,
} from "lucide-react"

import { usePagesList } from "../../api/cms/usePagesApi"
import { formatRelativeTime } from "../../utils/dateHelpers"
import styles from "./CardsSection.module.css"
import PageCard from "./PageCard"
import EditPageModal from "./EditPageModal"
import AddPageModal from "./AddPageModal"
import PageManagementHeader from "./PageManagmentHeader"

const CONTENT_ICONS = {
  destination: MapPin,
  blog: FileText,
  inspiration: Sparkles,
  campaign: Sparkles,
}

function mapPageToCard(apiPage) {
  if (!apiPage) return null
  const Icon = apiPage.pageCategory === "CONTENT" && apiPage.contentType
    ? (CONTENT_ICONS[apiPage.contentType] || FileText)
    : Home
  return {
    ...apiPage,
    Icon,
    status: apiPage.status === "PUBLISHED" ? "Published" : "Draft",
    seoTitle: apiPage.metaTitle || apiPage.title || "",
    seoDesc: apiPage.metaDescription || "",
    time: formatRelativeTime(apiPage.updatedAt),
    views: apiPage.viewCount ?? 0,
    category: apiPage.category ?? apiPage.contentType ?? null,
  }
}

const CardsSection = () => {
  const [activeTab, setActiveTab] = useState("static")
  const [search, setSearch] = useState("")
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [selectedPage, setSelectedPage] = useState(null)

  const { data, loading, error, refetch } = usePagesList({
    limit: 100,
    page: 1,
    search: search.trim() || undefined,
  })
  const allPages = Array.isArray(data) ? data : []

  const filteredPages = useMemo(() => {
    const category = activeTab === "static" ? "STATIC" : "CONTENT"
    return allPages.filter((p) => p.pageCategory === category)
  }, [allPages, activeTab])

  const cards = useMemo(
    () => filteredPages.map(mapPageToCard),
    [filteredPages]
  )

  const staticCount = useMemo(() => {
    if (!allPages) return 0
    return allPages.filter((p) => p.pageCategory === "STATIC").length
  }, [allPages])

  const contentCount = useMemo(() => {
    if (!allPages) return 0
    return allPages.filter((p) => p.pageCategory === "CONTENT").length
  }, [allPages])

  const handleEdit = (page) => {
    setSelectedPage(page)
    setIsEditOpen(true)
  }

  const handleAdd = () => {
    setIsAddOpen(true)
  }

  const handleModalClose = () => {
    setIsEditOpen(false)
    setIsAddOpen(false)
    setSelectedPage(null)
  }

  const handleSaveSuccess = () => {
    handleModalClose()
    refetch()
  }

  return (
    <div className={styles.wrapper}>
      <PageManagementHeader
        handleAdd={handleAdd}
        search={search}
        onSearchChange={setSearch}
        publishedCount={allPages.filter((p) => p.status === "PUBLISHED").length}
        draftCount={allPages.filter((p) => p.status === "DRAFT").length}
      />
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${
            activeTab === "static" ? styles.activeTab : ""
          }`}
          onClick={() => setActiveTab("static")}
        >
          Static Pages
          <span className={styles.count}>{staticCount}</span>
        </button>

        <button
          className={`${styles.tab} ${
            activeTab === "content" ? styles.activeContentTab : ""
          }`}
          onClick={() => setActiveTab("content")}
        >
          Content Pages
          <span className={styles.count}>{contentCount}</span>
        </button>
      </div>

      {loading && (
        <div className={styles.loading}>Loading pages…</div>
      )}
      {error && (
        <div className={styles.error}>{error}</div>
      )}
      {!loading && !error && (
        <div className={styles.tableSection}>
          {cards.length === 0 ? (
            <div className={styles.empty}>
              No {activeTab === "static" ? "static" : "content"} pages found.
              {search.trim() ? " Try a different search." : ""}
            </div>
          ) : (
            <div className={styles.grid}>
              {cards.map((page) => (
                <PageCard
                  key={page.id}
                  type={activeTab}
                  {...page}
                  onEdit={() => handleEdit(page)}
                />
              ))}
            </div>
          )}
          <div className={styles.footerBar}>
            <span className={styles.footerCount}>
              {cards.length} page{cards.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      <EditPageModal
        open={isEditOpen}
        onClose={handleModalClose}
        page={selectedPage}
        onSuccess={handleSaveSuccess}
      />
      <AddPageModal
        open={isAddOpen}
        onClose={handleModalClose}
        onSuccess={handleSaveSuccess}
      />
    </div>
  )
}

export default CardsSection
